/**
 * src/offline/uuid.ts — WO-M3
 *
 * Lightweight UUID v4 generator with no external dependencies.
 *
 * Uses `crypto.getRandomValues` (available in Hermes ≥0.11 / JavaScriptCore
 * on all supported React Native versions) to produce RFC-4122 v4 UUIDs.
 * Falls back to `Math.random` only if the global is absent — this keeps
 * the helper unit-testable in plain Node/Jest without polyfilling.
 *
 * PURE module — no native calls, no React imports. Fully testable in Node.
 * NEVER imports @smartbox/* (CLAUDE.md invariant 1).
 */

/**
 * Generate a UUID v4 string (e.g. "550e8400-e29b-41d4-a716-446655440000").
 *
 * Idempotency note: callers use this value as the `id` column of
 * `capture_queue`, making it the permanent client-side idempotency key.
 */
export function uuidV4(): string {
  const bytes = new Uint8Array(16);

  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.getRandomValues === 'function'
  ) {
    crypto.getRandomValues(bytes);
  } else {
    // Fallback for environments without Web Crypto (e.g. old Jest setups).
    for (let i = 0; i < 16; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }

  // Set version bits: v4 = 0100xxxx in byte 6
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  // Set variant bits: 10xxxxxx in byte 8
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;

  const hex = Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');

  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20),
  ].join('-');
}
